<script setup lang="ts">
import { reactive, computed, watch } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers, maxValue } from "@vuelidate/validators";

import { EditableList } from "@/components/EditableList";

import { Input, Question, DatePicker, Radio, RadioSet } from "@/components/fields";
import { isNumber } from "@/utils";
import { CholesterolReading } from "@/entities/conditions";
import readingValidator from "./validators";
import { formatDate } from "@/utils/date";

export interface CholesterolFormState {
    currentReading: CholesterolReading | null,
    showCloseModal: boolean,
    isEdit: boolean,
}

export interface CholesterolFormProps {
    modelValue: CholesterolReading[],
    diagnosisDate: string | null
}

const props = defineProps<CholesterolFormProps>();

const emit = defineEmits<{
    (on:"update:modelValue", value: CholesterolReading[]): void
}>();

const state = reactive<CholesterolFormState>({
    currentReading: null,
    showCloseModal: false,
    isEdit: false,
});

const validation = readingValidator(state);

const rules = computed(() => {
    if (state.currentReading?.cholesterolReadingsType === "0") {
        return {
            notExceed: {
                maxValue: helpers.withMessage("The combined value for HDL and LDL must not exceed 999.999 mmol/L.",
                    maxValue(999.999)),
            },
        };
    }

    return {
        notExceed: {},
    };
});

const notExceed = computed(() => {
    return Number(state.currentReading?.HDL) + Number(state.currentReading?.LDL) || 0;
});
const v$ = useVuelidate(rules, {
    notExceed,
});

watch(state, (newValue) => {
    if (state.currentReading) {
        if (newValue.currentReading?.cholesterolReadingsType === "0") {
            state.currentReading.TOTAL = null;
        } else {
            state.currentReading.HDL = null;
            state.currentReading.LDL = null;
        }
    }
});

watch(notExceed, () => {
    v$.value.notExceed.$touch();
});

const addReading = () => {
    state.currentReading = {
        syntheticId: Date.now(),
        cholesterolReadingsType: null,
        LDL: null,
        HDL: null,
        TOTAL: null,
        readingdate: null,
    };
};

const handleUpdate = (items: CholesterolReading[]) => {
    emit("update:modelValue", items);
};

const handleUpdateItem = (item: CholesterolReading | null) => {
    state.currentReading = item;
};

const handleSave = (items: CholesterolReading[]) => {
    const isInvalid = v$.value.notExceed.$invalid;
    if (!isInvalid) {
        state.currentReading = null;
        emit("update:modelValue", items);
    }
};
const currentDate = new Date();
const maxDate = currentDate;

</script>
<template>
    <EditableList
        :current-item="state.currentReading"
        :items="modelValue"
        header="High Cholesterol Reading"
        @update:items="handleUpdate"
        @update:item="handleUpdateItem"
        @save="handleSave"
    >
        <template
            v-for="item in modelValue"
            #[item.syntheticId]
            :key="item.syntheticId"
        >
            <span
                v-if="item.TOTAL"
                class="col-md-2"
            >
                Total: {{ item.TOTAL }}
            </span>
            <span
                v-else
                class="col-md-2"
            >
                LDL: {{ item.LDL }} HDL: {{ item.HDL }}
            </span>
            <span class="mx-4 col-md-2">
                {{ formatDate(item.readingdate) }}
            </span>
        </template>
        <template
            v-if="state.currentReading"
            #default
        >
            <p class="font-weight-bold-col">
                About Your High Cholesterol
            </p>
            <p class="alert alert-info">
                Cholesterol readings can be a
                <strong> total reading </strong>
                of both HDL and LDL or it can be broken down into two readings
                <strong> HDL </strong> (Good Cholesterol) and <strong> LDL </strong> (Bad Cholesterol)
            </p>
            <Question
                name="cholesterolReadingsType"
                label="What type of reading do you want to enter?"
            >
                <RadioSet>
                    <Radio
                        v-model="state.currentReading.cholesterolReadingsType"
                        title="Total reading"
                        value="1"
                        :validation="validation.cholesterolReadingsType"
                    />
                    <Radio
                        v-model="state.currentReading.cholesterolReadingsType"
                        title="HDL and LDL"
                        value="0"
                        :validation="validation.cholesterolReadingsType"
                    />
                </RadioSet>
            </Question>
            <template v-if="state.currentReading.cholesterolReadingsType === '0'">
                <Question
                    name="LDL"
                    label="LDL Cholesterol reading (mmol/L)"
                >
                    <Input
                        v-model.number="state.currentReading.LDL"
                        type="number"
                        inputmode="numeric"
                        placeholder="LDL"
                        :filter="isNumber"
                        :validation="validation.LDL"
                    />
                </Question>
                <Question
                    name="HDL"
                    label="HDL Cholesterol reading (mmol/L)"
                >
                    <Input
                        v-model.number="state.currentReading.HDL"
                        type="number"
                        inputmode="numeric"
                        placeholder="HDL"
                        :filter="isNumber"
                        :validation="validation.HDL"
                    />
                </Question>
            </template>
            <Question
                v-else-if="state.currentReading.cholesterolReadingsType === '1'"
                name="TOTAL"
                label="Total Cholesterol reading (mmol/L)"
            >
                <Input
                    v-model.number="state.currentReading.TOTAL"
                    type="number"
                    inputmode="numeric"
                    placeholder="Total Cholesterol reading"
                    :filter="isNumber"
                    :validation="validation.TOTAL"
                />
            </Question>
            <Question
                name="readingdate"
                label="Reading date"
            >
                <DatePicker
                    id="readingdate"
                    :key="state.currentReading.syntheticId"
                    v-model="state.currentReading.readingdate"
                    :max-date="maxDate"
                    :min-date="new Date(props.diagnosisDate || 0)"
                    :show-day-field="false"
                    :validation="validation.readingdate"
                />
            </Question>
            <div
                v-if="v$.notExceed.$error"
                class="alert alert-danger text-center d-block"
            >
                {{ v$.notExceed.$errors[0].$message }}
            </div>
        </template>
    </EditableList>
    <button
        v-if="state.currentReading === null"
        id="add-cholesterol-reading"
        type="button"
        class="btn btn-primary btn-block font-weight-bold align-self-center mb-2"
        @click.prevent="addReading"
    >
        {{ modelValue.length ? "Add another Reading" : "Add a reading" }}
    </button>
</template>
